import { MonitoringDevice } from "models/monitoring-device";
import { AscmuSubscription } from "models/ascmu-subscription";
import {
  axiosInstance as Repository,
  RESULT_CD,
  RepositoryError,
} from "../repository";

const RESOURCE = "/projects";

/* カスタム例外 */
class MonitoringDevicesRepositoryError extends RepositoryError {
  constructor(message) {
    super(message);
    this.name = this.constructor.name;
    this.errorCd = MonitoringDevicesRepositoryError.getErrorCd();
  }

  static getErrorCd() {
    return "08000";
  }
}

class MonitoringDeviceNotFoundError extends RepositoryError {
  constructor(message) {
    super(message);
    this.name = this.constructor.name;
    this.errorCd = MonitoringDeviceNotFoundError.getErrorCd();
  }

  static getErrorCd() {
    return "08001";
  }
}

function getInstance(item) {
  const projectId = item.project_id;
  const monitoringDeviceId = item.monitoring_device_id;
  const monitoringDevice = new MonitoringDevice(projectId, monitoringDeviceId);

  monitoringDevice.organizationId = item.organization_id;
  monitoringDevice.productKey = item.product_key;
  monitoringDevice.name = item.name;
  monitoringDevice.status = item.status;
  monitoringDevice.deletedFlag = item.deleted_flag;
  monitoringDevice.version = item.version;
  monitoringDevice.resourceId = item.resource_id;
  monitoringDevice.createdAt = item.created_at;
  monitoringDevice.createdBy = item.created_by;
  monitoringDevice.updatedAt = item.updated_at;
  monitoringDevice.updatedBy = item.updated_by;
  monitoringDevice.deletedAt = item.deleted_at;
  monitoringDevice.deletedBy = item.deleted_by;

  return monitoringDevice;
}

function makeRequestJson(monitoringDevice) {
  const requestJosn = {
    organization_id: monitoringDevice.organizationId,
    product_key: monitoringDevice.productKey,
    name: monitoringDevice.name,
    status: monitoringDevice.status,
    deleted_flag: monitoringDevice.deletedFlag,
    version: monitoringDevice.version,
    resource_id: monitoringDevice.resourceId,
    created_at: monitoringDevice.createdAt,
    created_by: monitoringDevice.createdBy,
    updated_at: monitoringDevice.updatedAt,
    updated_by: monitoringDevice.updatedBy,
    deleted_at: monitoringDevice.deletedAt,
    deleted_by: monitoringDevice.deletedBy,
  };

  return requestJosn;
}

const MonitoringDevicesRepository = {

  async store(monitoringDevice) {
    const urlParams = {
      projectId: monitoringDevice.projectId,
    };
    const requestParams = makeRequestJson(monitoringDevice);
    const response = await Repository.post(`${RESOURCE}/${urlParams.projectId}/monitoring-devices`, requestParams);

    // 処理結果の確認
    if (response.data.result_cd !== RESULT_CD.SUCCESS) {
      throw new MonitoringDevicesRepositoryError();
    }

    return getInstance(response.data.item);
  },

  async update(monitoringDevice) {
    const urlParams = {
      projectId: monitoringDevice.projectId,
    };
    urlParams.monitoringDeviceId = monitoringDevice.monitoringDeviceId;
    const requestParams = makeRequestJson(monitoringDevice);
    const response = await Repository.put(`${RESOURCE}/${urlParams.projectId}/monitoring-devices/${urlParams.monitoringDeviceId}`, requestParams);

    // 処理結果の確認
    if (response.data.result_cd !== RESULT_CD.SUCCESS) {
      throw new MonitoringDevicesRepositoryError();
    }

    return getInstance(response.data.item);
  },

  async index(projectId) {
    const urlParams = {
      projectId,
    };
    const response = await Repository.get(`${RESOURCE}/${urlParams.projectId}/monitoring-devices`, {
      params: {},
    });

    // 処理結果の確認
    if (response.data.result_cd !== RESULT_CD.SUCCESS) {
      throw new MonitoringDevicesRepositoryError();
    }

    const items = [];
    response.data.items.forEach((item) => {
      items.push(getInstance(item));
    });
    return items;
  },

  async get(projectId, monitoringDeviceId) {
    const urlParams = {
      projectId,
    };
    urlParams.monitoringDeviceId = monitoringDeviceId;
    const response = await Repository.get(`${RESOURCE}/${urlParams.projectId}/monitoring-devices/${urlParams.monitoringDeviceId}`, {
      params: {},
    });

    // 処理結果の確認
    if (response.data.result_cd !== RESULT_CD.SUCCESS) {
      throw new MonitoringDevicesRepositoryError();
    }

    return getInstance(response.data.item);
  },

  async destroy(projectId, monitoringDeviceId) {
    const urlParams = {
      projectId,
    };
    urlParams.monitoringDeviceId = monitoringDeviceId;
    const response = await Repository.delete(`${RESOURCE}/${urlParams.projectId}/monitoring-devices/${urlParams.monitoringDeviceId}`, {
      params: {},
    });

    // 処理結果の確認
    if (response.data.result_cd !== RESULT_CD.SUCCESS) {
      throw new MonitoringDevicesRepositoryError();
    }
  },

  async upsert(monitoringDevice) {
    let res = null;
    if (monitoringDevice.monitoringDeviceId === null) {
      res = this.store(monitoringDevice);
    } else {
      res = this.update(monitoringDevice);
    }
    return res;
  },

  async getAvaiableList(projectId) {
    const urlParams = {
      projectId,
    };
    const response = await Repository.get(`${RESOURCE}/${urlParams.projectId}/monitoring-devices/avaliable`, {
      params: {},
    });

    // 処理結果の確認
    if (response.data.result_cd !== RESULT_CD.SUCCESS) {
      throw new MonitoringDevicesRepositoryError();
    }

    const items = [];
    response.data.items.forEach((item) => {
      items.push(getInstance(item));
    });

    return items;
  },

  async getAscmuSubscriptionHistory(projectId, monitoringDeviceId) {
    const urlParams = {
      projectId,
      monitoringDeviceId,
    };
    const response = await Repository.get(`${RESOURCE}/${urlParams.projectId}/monitoring-devices/${urlParams.monitoringDeviceId}/ascmu-subscription-history`, {
      params: {},
    });

    // 処理結果の確認
    if (response.data.result_cd !== RESULT_CD.SUCCESS) {
      if (response.data.errors[0].error_cd === MonitoringDeviceNotFoundError.getErrorCd()) {
        throw new MonitoringDeviceNotFoundError();
      } else {
        throw new MonitoringDevicesRepositoryError();
      }
    }

    // ASCMUサブスクリプションリスト
    const items = [];
    response.data.items.forEach((item) => {
      const organizationId = item.organization_id;
      const subscriptionId = item.subscription_id;
      const subscription = new AscmuSubscription(organizationId, subscriptionId);
      subscription.setApiResponseJosn(item);
      items.push(subscription);
    });
    return items;
  },

  async createAscmuSubscription(projectId, monitoringDeviceId) {
    const urlParams = {
      projectId,
      monitoringDeviceId,
    };
    const requestParams = {};
    const response = await Repository.post(
      `${RESOURCE}/${urlParams.projectId}/monitoring-devices/${urlParams.monitoringDeviceId}/create-ascmu-subscription`,
      requestParams,
    );

    // 処理結果の確認
    if (response.data.result_cd !== RESULT_CD.SUCCESS) {
      if (response.data.errors[0].error_cd === MonitoringDeviceNotFoundError.getErrorCd()) {
        throw new MonitoringDeviceNotFoundError();
      } else {
        throw new MonitoringDevicesRepositoryError();
      }
    }

    // ASCMUサブスクリプションリスト
    const { item } = response.data;
    const subscription = new AscmuSubscription(
      response.data.item.organization_id,
      response.data.item.subscription_id,
    );
    subscription.setApiResponseJosn(item);
    return subscription;
  },
};

export {
  MonitoringDevicesRepository,
  MonitoringDevicesRepositoryError,
};
